import { useEffect, useState } from 'react'
import {
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  VStack,
  Link,
  Text,
  Button,
  useToast,
  Box,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation, Trans, TFunction } from 'next-i18next'

import { PRIVACY_POLICY, TERMS_OF_USE } from '@/routes'
import {
  DEFAULT_TOAST_OPTIONS,
  getErrorToastObject,
} from '@/lib/utils/format/toast'

import { Turnstile } from '@/features/landing/components/Form/CloudflareTurnstileWidget/Turnstile'

import { schema, FormValue } from './schema'

const defaultValues = {
  companyName: '',
  companyEmail: '',
  firstName: '',
  lastName: '',
  tel: '',
  jobTitle: '',
  services: {
    implementation: false,
    advisory: false,
    training: false,
    legal: false,
    compliance: false,
    dpo: false,
  },
  acceptedTerm: false,
  agreedToMarketingConsent: false,
  url: '',
}

const formLabelStyle = {
  mb: 2,
}

const linkStyle = {
  textDecoration: 'underline !important',
  '&:hover': {
    color: 'whiteAlpha.800',
  },
  '&:focus': {
    color: 'whiteAlpha.700',
  },
  '&:active': {
    color: 'whiteAlpha.500',
  },
}

const formErrorStyle = {
  color: 'gray.1000',
}
interface Props {
  id?: string
  resourceTitle?: string
  onSubmit: (data: FormValue) => Promise<void>
  formsubmitButton?: string
  trans?: TFunction
}

function Form({
  id,
  resourceTitle = '',
  onSubmit,
  formsubmitButton,
  trans,
}: Props): React.ReactElement {
  const { t: tResource } = useTranslation('resource')
  const {
    t: tCommon,
    i18n: { language },
  } = useTranslation('common')

  const toast = useToast()

  const {
    register,
    watch,
    handleSubmit,
    reset: resetForm,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { ...defaultValues, resourceTitle },
    resolver: zodResolver(schema),
  })

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleVerify = (token: string) => {
    setTurnstileToken(token);
  };

  const _onSubmit = async (data: FormValue) => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (isSubmitting) return

    if (!turnstileToken) {
      toast({
        ...DEFAULT_TOAST_OPTIONS,
        status: 'error',
        description: tCommon('errors.missingHumanVerification'),
      });
      return;
    }

    try {
      await onSubmit({
        ...data,
        turnstileToken,
        url,
      })

      resetForm()
      setTurnstileToken(null)
    } catch (error: any) {
      toast(getErrorToastObject(error))
    }
  }

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      toast({
        ...DEFAULT_TOAST_OPTIONS,
        status: 'error',
        description: tCommon('errors.incorrectData'),
      })
    }
  }, [errors])

  return (
    <Box
      id={id}
      sx={{
        alignSelf: { base: 'stretch', md: 'flex-start' },
        w: 'full',
        bgColor: 'secondary.800',
        bgImage:
          'linear-gradient(94.35deg, rgba(70, 158, 255, 0.8) 0%, rgba(210, 83, 255, 0.8) 100%)',
        borderRadius: {
          base: 'none',
          md: 'md',
        },
      }}
    >
      <VStack
        layerStyle={{ base: 'container', md: '' }}
        spacing="10"
        sx={{
          pt: {
            base: 24,
            md: 10,
          },
          pb: {
            base: 16,
            md: 10,
          },
          alignItems: 'stretch',
          w: 'full',
          color: 'white',
          '& input': {
            color: 'gray.900',
          },
          pl: {
            md: 8,
            lg: 8,
          },
          pr: {
            md: 8,
            lg: 8,
          },
        }}
      >
        <Text
          as="h2"
          sx={{
            fontSize: {
              base: '2xl',
              sm: '4xl',
              md: '2xl',
            },
            fontWeight: 'medium',
            textAlign: {
              base: 'center',
              md: 'left',
            },
          }}
        >
          <Trans t={trans ?? tResource} i18nKey="form.title">
            เตรียมความพร้อม PDPA ให้องค์กรของคุณ
          </Trans>
        </Text>
        <form onSubmit={handleSubmit(_onSubmit)}>
          <VStack spacing="10" sx={{ alignItems: 'stretch' }}>
            <VStack spacing="4">
              <FormControl isRequired isInvalid={Boolean(errors.companyEmail)}>
                <FormLabel sx={formLabelStyle}>
                  {tResource('form.fields.email.label')}
                </FormLabel>
                <Input
                  placeholder={tResource('form.fields.email.placeholder')}
                  type="email"
                  {...register('companyEmail')}
                />
                {errors.companyEmail && (
                  <FormErrorMessage sx={formErrorStyle}>
                    {errors.companyEmail.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl isRequired isInvalid={Boolean(errors.companyName)}>
                <FormLabel sx={formLabelStyle}>
                  {tResource('form.fields.company.label')}
                </FormLabel>
                <Input
                  placeholder={tResource('form.fields.company.placeholder')}
                  {...register('companyName')}
                />
                {errors.companyName && (
                  <FormErrorMessage sx={formErrorStyle}>
                    {errors.companyName.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl isRequired isInvalid={Boolean(errors.firstName)}>
                <FormLabel sx={formLabelStyle}>
                  {tResource('form.fields.firstName.label')}
                </FormLabel>
                <Input
                  placeholder={tResource('form.fields.firstName.placeholder')}
                  {...register('firstName')}
                />
                {errors.firstName && (
                  <FormErrorMessage sx={formErrorStyle}>
                    {errors.firstName.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl isRequired isInvalid={Boolean(errors.lastName)}>
                <FormLabel sx={formLabelStyle}>
                  {tResource('form.fields.lastName.label')}
                </FormLabel>
                <Input
                  placeholder={tResource('form.fields.lastName.placeholder')}
                  {...register('lastName')}
                />
                {errors.lastName && (
                  <FormErrorMessage sx={formErrorStyle}>
                    {errors.lastName.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl isRequired isInvalid={Boolean(errors.tel)}>
                <FormLabel sx={formLabelStyle}>
                  {tResource('form.fields.tel.label')}
                </FormLabel>
                <Input
                  placeholder={tResource('form.fields.tel.placeholder')}
                  type="tel"
                  {...register('tel')}
                />
                {errors.tel && (
                  <FormErrorMessage sx={formErrorStyle}>
                    {errors.tel.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl isInvalid={Boolean(errors.jobTitle)}>
                <FormLabel sx={formLabelStyle}>
                  {tResource('form.fields.jobTitle.label')}
                </FormLabel>
                <Input
                  placeholder={tResource('form.fields.jobTitle.placeholder')}
                  {...register('jobTitle')}
                />
                {errors.jobTitle && (
                  <FormErrorMessage sx={formErrorStyle}>
                    {errors.jobTitle.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl isInvalid={Boolean(errors.services)}>
                <FormLabel sx={formLabelStyle}>
                  {tResource('form.fields.services.label')}
                </FormLabel>
                <VStack
                  spacing="4"
                  sx={{ alignItems: 'flex-start', fontWeight: 'medium' }}
                >
                  <Checkbox
                    colorScheme="blackAlpha"
                    {...register('services.implementation')}
                    isChecked={watch('services.implementation')}
                  >
                    {tResource('form.fields.services.options.0')}
                  </Checkbox>
                  <Checkbox
                    colorScheme="blackAlpha"
                    {...register('services.advisory')}
                    isChecked={watch('services.advisory')}
                  >
                    {tResource('form.fields.services.options.1')}
                  </Checkbox>
                  <Checkbox
                    colorScheme="blackAlpha"
                    {...register('services.training')}
                    isChecked={watch('services.training')}
                  >
                    {tResource('form.fields.services.options.2')}
                  </Checkbox>
                  <Checkbox
                    colorScheme="blackAlpha"
                    {...register('services.legal')}
                    isChecked={watch('services.legal')}
                  >
                    {tResource('form.fields.services.options.3')}
                  </Checkbox>
                  <Checkbox
                    colorScheme="blackAlpha"
                    {...register('services.compliance')}
                    isChecked={watch('services.compliance')}
                  >
                    {tResource('form.fields.services.options.4')}
                  </Checkbox>
                  <Checkbox
                    colorScheme="blackAlpha"
                    {...register('services.dpo')}
                    isChecked={watch('services.dpo')}
                  >
                    {tResource('form.fields.services.options.5')}
                  </Checkbox>
                </VStack>
                {errors.services && (
                  <FormErrorMessage sx={formErrorStyle}>
                    {errors.services.implementation?.message ||
                      errors.services.advisory?.message ||
                      errors.services.training?.message ||
                      errors.services.legal?.message ||
                      errors.services.compliance?.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            </VStack>
            <VStack
              spacing="4"
              sx={{
                alignItems: 'flex-start',
                fontWeight: 'medium',
                gridColumn: { md: '1 / 3' },
              }}
            >
              <Checkbox
                isRequired
                colorScheme="blackAlpha"
                {...register('acceptedTerm')}
                isChecked={watch('acceptedTerm')}
                sx={{ alignItems: 'flex-start' }}
              >
                <Trans t={tResource} i18nKey="form.fields.agreedToTerms">
                  ฉันยอมรับ
                  <Link
                    isExternal
                    sx={linkStyle}
                    href={`/${language}${TERMS_OF_USE.pathname}`}
                  >
                    เงื่อนไขในการใช้เว็บไซต์
                  </Link>
                  และ
                  <Link
                    isExternal
                    sx={linkStyle}
                    href={`/${language}${PRIVACY_POLICY.pathname}`}
                  >
                    นโยบายความเป็นส่วนตัว
                  </Link>
                </Trans>
              </Checkbox>
              <Checkbox
                colorScheme="blackAlpha"
                {...register('agreedToMarketingConsent')}
                isChecked={watch('agreedToMarketingConsent')}
                sx={{ alignItems: 'flex-start' }}
              >
                <Trans
                  t={tResource}
                  i18nKey="getQuotation.form.marketingConsent"
                >
                  ฉันต้องการรับโปรโมชันและข่าวสารทางการตลาดเกี่ยวกับ&nbsp; PDPA
                  Core &nbsp;รวมถึงบริการอื่น ๆ ที่เกี่ยวข้องกับ PDPA <br />
                  (คุณสามารถยกเลิกได้ทุกเมื่อ)
                </Trans>
              </Checkbox>
            </VStack>
            <Box mt={4} width="100%" display="flex" justifyContent="center">
              <Turnstile
                onVerify={handleVerify}
                theme="light"
                action="quotation_form_submit"
              />
            </Box>
            <Button
              isLoading={isSubmitting}
              type="submit"
              size="xl"
              colorScheme="blackAlpha"
              sx={{
                alignSelf: 'stretch',
                w: 'full',
                mx: 'auto',
                bg: 'gray.900',
                px: 10,
              }}
            >
              {formsubmitButton
                ? formsubmitButton
                : tResource('form.fields.submitButton')}
            </Button>
          </VStack>
        </form>
      </VStack>
    </Box>
  )
}

export default Form
